import clsx from "clsx";

interface SpinnerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
}

export default function Spinner({ size = "md", className }: SpinnerProps) {
  const sizeMap = {
    sm: { container: "w-12 h-14", cup: "w-8 h-6", steam: "h-4" },
    md: { container: "w-20 h-24", cup: "w-14 h-10", steam: "h-6" },
    lg: { container: "w-28 h-32", cup: "w-20 h-14", steam: "h-8" },
  };

  const s = sizeMap[size];

  return (
    <div className={clsx("flex flex-col items-center justify-center", s.container, className)}>
      {/* Steam lines */}
      <div className={clsx("flex items-end justify-center gap-1.5 mb-1", s.steam)}>
        <div className="mocaccino-steam mocaccino-steam-1" />
        <div className="mocaccino-steam mocaccino-steam-2" />
        <div className="mocaccino-steam mocaccino-steam-3" />
      </div>

      {/* Coffee cup */}
      <div className="relative">
        <svg viewBox="0 0 64 48" className={s.cup} fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Cup body */}
          <path
            d="M8 4h36v28c0 8.837-7.163 16-16 16H24c-8.837 0-16-7.163-16-16V4z"
            fill="var(--text-tertiary)"
            fillOpacity={0.15}
            stroke="var(--text-secondary)"
            strokeWidth="2.5"
          />
          {/* Coffee liquid */}
          <path
            d="M10 16h32v16c0 7.732-6.268 14-14 14H24c-7.732 0-14-6.268-14-14V16z"
            className="mocaccino-liquid"
          />
          {/* Cup handle */}
          <path
            d="M44 12h4c5.523 0 10 4.477 10 10v0c0 5.523-4.477 10-10 10h-4"
            stroke="var(--text-secondary)"
            strokeWidth="2.5"
            strokeLinecap="round"
            fill="none"
          />
          {/* Saucer */}
          <ellipse
            cx="26"
            cy="47"
            rx="24"
            ry="3"
            fill="var(--text-tertiary)"
            fillOpacity={0.12}
          />
        </svg>

        {/* Sparkle particles */}
        <div className="mocaccino-sparkle mocaccino-sparkle-1" />
        <div className="mocaccino-sparkle mocaccino-sparkle-2" />
        <div className="mocaccino-sparkle mocaccino-sparkle-3" />
        <div className="mocaccino-sparkle mocaccino-sparkle-4" />
      </div>

      {/* CSS for animations */}
      <style>{`
        .mocaccino-steam {
          width: 2px;
          border-radius: 1px;
          opacity: 0.5;
        }
        .mocaccino-steam-1 {
          height: 60%;
          background: linear-gradient(to top, #84cc16, transparent);
          animation: mocaccino-steam 1.8s ease-in-out infinite;
        }
        .mocaccino-steam-2 {
          height: 80%;
          background: linear-gradient(to top, #a3e635, transparent);
          animation: mocaccino-steam 1.8s ease-in-out infinite 0.3s;
        }
        .mocaccino-steam-3 {
          height: 55%;
          background: linear-gradient(to top, #65a30d, transparent);
          animation: mocaccino-steam 1.8s ease-in-out infinite 0.6s;
        }
        @keyframes mocaccino-steam {
          0%, 100% {
            opacity: 0.3;
            transform: translateY(0) scaleY(0.8);
          }
          50% {
            opacity: 0.7;
            transform: translateY(-4px) scaleY(1.2);
          }
        }
        .mocaccino-liquid {
          fill: #84cc16;
          opacity: 0.3;
          animation: mocaccino-fill 2.4s ease-in-out infinite;
        }
        @keyframes mocaccino-fill {
          0%, 100% { opacity: 0.25; }
          50% { opacity: 0.45; }
        }
        .mocaccino-sparkle {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
        }
        .mocaccino-sparkle-1 {
          width: 4px; height: 4px;
          background: #84cc16;
          top: 10%; left: 20%;
          animation: mocaccino-sparkle-rise 2s ease-out infinite;
        }
        .mocaccino-sparkle-2 {
          width: 3px; height: 3px;
          background: #a3e635;
          top: 15%; right: 25%;
          animation: mocaccino-sparkle-rise 2s ease-out infinite 0.5s;
        }
        .mocaccino-sparkle-3 {
          width: 3px; height: 3px;
          background: #65a30d;
          top: 5%; left: 45%;
          animation: mocaccino-sparkle-rise 2s ease-out infinite 1s;
        }
        .mocaccino-sparkle-4 {
          width: 2px; height: 2px;
          background: #84cc16;
          top: 20%; right: 35%;
          animation: mocaccino-sparkle-rise 2s ease-out infinite 1.5s;
        }
        @keyframes mocaccino-sparkle-rise {
          0% {
            opacity: 0;
            transform: translateY(0) scale(0);
          }
          30% {
            opacity: 1;
            transform: translateY(-8px) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-24px) scale(0.3);
          }
        }
      `}</style>
    </div>
  );
}

export function FullPageSpinner() {
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-white/80 dark:bg-[#0a0f1a]/85 backdrop-blur-md z-50 gap-4">
      <Spinner size="lg" />
      <p className="text-sm text-[var(--text-tertiary)] animate-pulse">Brewing your experience...</p>
    </div>
  );
}
